// Marketing front page for Amanar Lead Engine.
//
// Committed dark treatment (matches the product's intelligence-terminal look).
// The hero's live-registration feed literally demonstrates the product: new
// Florida businesses appear at the top with their stage badge, the same signal
// the app sells. Gated on prefers-reduced-motion.

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const CSS = `
.lp {
  --ground: #0B1120; --panel: #131C2E; --panel-2: #1B2740;
  --ink: #F8FAFC; --body: #C3D0E2; --muted: #7E90AC; --line: #24324B;
  --blue: #3B82F6; --blue-hi: #60A5FA; --emerald: #10B981; --gold: #FCD34D;
  --mono: ui-monospace, "SF Mono", "SFMono-Regular", Menlo, Consolas, monospace;
  --sans: -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif;
  background: var(--ground); color: var(--body); font-family: var(--sans);
  min-height: 100vh; overflow-x: hidden;
}
.lp * { box-sizing: border-box; }
.lp a { color: inherit; text-decoration: none; }
.lp-wrap { max-width: 1120px; margin: 0 auto; padding: 0 24px; }

/* Nav */
.lp-nav { display: flex; align-items: center; justify-content: space-between;
  padding: 20px 24px; max-width: 1120px; margin: 0 auto; }
.lp-logo { font-weight: 800; letter-spacing: 2px; color: var(--ink); font-size: 0.95rem; }
.lp-logo span { color: var(--blue-hi); }
.lp-nav-actions { display: flex; align-items: center; gap: 14px; }
.lp-signin { color: var(--body); font-size: 0.9rem; font-weight: 600; }
.lp-signin:hover { color: var(--ink); }

.lp-btn { display: inline-flex; align-items: center; gap: 8px; font-weight: 700;
  border-radius: 9px; cursor: pointer; border: 1px solid transparent; font-size: 0.92rem;
  padding: 11px 20px; transition: transform .12s ease, background .15s ease; font-family: var(--sans); }
.lp-btn:hover { transform: translateY(-1px); }
.lp-btn-primary { background: var(--blue); color: #fff; box-shadow: 0 6px 20px rgba(59,130,246,.28); }
.lp-btn-primary:hover { background: var(--blue-hi); }
.lp-btn-ghost { background: transparent; color: var(--ink); border-color: var(--line); }
.lp-btn-ghost:hover { border-color: var(--blue); }
.lp-btn-lg { padding: 14px 26px; font-size: 1rem; }

/* Hero */
.lp-hero { display: grid; grid-template-columns: 1.05fr 0.95fr; gap: 56px;
  align-items: center; padding: 56px 0 40px; }
.lp-eyebrow { display: inline-flex; align-items: center; gap: 8px; font-family: var(--mono);
  font-size: 0.72rem; letter-spacing: 1px; color: var(--emerald); text-transform: uppercase;
  border: 1px solid var(--line); background: rgba(16,185,129,.06); border-radius: 999px;
  padding: 6px 13px; margin-bottom: 22px; }
.lp-dot { width: 7px; height: 7px; border-radius: 50%; background: var(--emerald);
  box-shadow: 0 0 0 0 rgba(16,185,129,.6); animation: lp-pulse 2s infinite; }
@keyframes lp-pulse { 0% { box-shadow: 0 0 0 0 rgba(16,185,129,.5); }
  70% { box-shadow: 0 0 0 7px rgba(16,185,129,0); } 100% { box-shadow: 0 0 0 0 rgba(16,185,129,0); } }
.lp-h1 { font-size: clamp(2.3rem, 5vw, 3.5rem); line-height: 1.04; font-weight: 800;
  letter-spacing: -1.5px; color: var(--ink); margin: 0 0 20px; text-wrap: balance; }
.lp-h1 em { font-style: normal; color: var(--gold); }
.lp-lede { font-size: 1.12rem; line-height: 1.6; max-width: 34ch; margin: 0 0 30px; }
.lp-hero-cta { display: flex; gap: 14px; flex-wrap: wrap; align-items: center; }
.lp-hero-note { font-family: var(--mono); font-size: 0.78rem; color: var(--muted); margin-top: 16px; }

/* Live feed */
.lp-feed { background: linear-gradient(180deg, var(--panel), var(--ground));
  border: 1px solid var(--line); border-radius: 16px; padding: 6px;
  box-shadow: 0 24px 60px rgba(0,0,0,.45); }
.lp-feed-head { display: flex; align-items: center; justify-content: space-between;
  padding: 12px 14px 10px; border-bottom: 1px solid var(--line); }
.lp-feed-title { font-family: var(--mono); font-size: 0.74rem; color: var(--muted);
  text-transform: uppercase; letter-spacing: 1px; }
.lp-feed-live { display: inline-flex; align-items: center; gap: 6px; font-family: var(--mono);
  font-size: 0.7rem; color: var(--emerald); }
.lp-feed-list { list-style: none; margin: 0; padding: 6px; display: flex; flex-direction: column; gap: 6px; }
.lp-row { display: flex; align-items: center; gap: 12px; padding: 12px 12px;
  border-radius: 10px; background: var(--panel-2); border: 1px solid transparent; }
.lp-row.fresh { animation: lp-slide .5s ease both; border-color: rgba(96,165,250,.35); }
@keyframes lp-slide { from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); } }
.lp-row-main { min-width: 0; flex: 1; }
.lp-row-name { color: var(--ink); font-weight: 650; font-size: 0.92rem; white-space: nowrap;
  overflow: hidden; text-overflow: ellipsis; }
.lp-row-meta { font-family: var(--mono); font-size: 0.72rem; color: var(--muted); margin-top: 2px; }
.lp-row-time { font-family: var(--mono); font-size: 0.7rem; color: var(--muted); flex-shrink: 0; }
.lp-badge { flex-shrink: 0; font-size: 0.6rem; font-weight: 800; letter-spacing: .4px;
  text-transform: uppercase; padding: 3px 8px; border-radius: 999px; }
.b-gold { background: var(--gold); color: #78350F; }
.b-green { background: #166534; color: #DCFCE7; }
.b-blue { background: #1E40AF; color: #DBEAFE; }

/* Stat strip */
.lp-stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 1px;
  background: var(--line); border: 1px solid var(--line); border-radius: 14px; overflow: hidden; margin: 24px 0 8px; }
.lp-stat { background: var(--ground); padding: 22px 20px; }
.lp-stat-n { font-family: var(--mono); font-size: 1.7rem; font-weight: 700; color: var(--ink); letter-spacing: -1px; }
.lp-stat-l { font-size: 0.82rem; color: var(--muted); margin-top: 4px; }

/* Sections */
.lp-section { padding: 64px 0; }
.lp-kicker { font-family: var(--mono); font-size: 0.74rem; letter-spacing: 1.5px; text-transform: uppercase;
  color: var(--blue-hi); margin-bottom: 12px; }
.lp-h2 { font-size: clamp(1.6rem, 3.5vw, 2.2rem); font-weight: 800; letter-spacing: -0.8px;
  color: var(--ink); margin: 0 0 14px; text-wrap: balance; }
.lp-sub { font-size: 1.05rem; max-width: 60ch; margin: 0 0 36px; }

/* Signal cards (differentiator) */
.lp-signals { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; }
.lp-signal { background: var(--panel); border: 1px solid var(--line); border-radius: 14px; padding: 20px; }
.lp-signal.spark { border-color: rgba(252,211,77,.4); background: linear-gradient(180deg, rgba(252,211,77,.06), var(--panel)); }
.lp-signal .lp-badge { display: inline-block; margin-bottom: 12px; }
.lp-signal h3 { color: var(--ink); font-size: 1rem; margin: 0 0 6px; }
.lp-signal p { font-size: 0.9rem; margin: 0; line-height: 1.5; }

/* Steps */
.lp-steps { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
.lp-step { border-left: 2px solid var(--line); padding: 4px 0 4px 20px; }
.lp-step-n { font-family: var(--mono); font-size: 0.8rem; color: var(--blue-hi); font-weight: 700; }
.lp-step h3 { color: var(--ink); font-size: 1.1rem; margin: 8px 0 6px; }
.lp-step p { font-size: 0.92rem; margin: 0; line-height: 1.55; }

/* Categories */
.lp-cats { display: flex; flex-wrap: wrap; gap: 10px; }
.lp-cat { border: 1px solid var(--line); background: var(--panel); color: var(--body);
  border-radius: 999px; padding: 9px 16px; font-size: 0.88rem; transition: border-color .15s; }
.lp-cat:hover { border-color: var(--blue); color: var(--ink); }

/* Pricing */
.lp-prices { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; align-items: start; }
.lp-price { background: var(--panel); border: 1px solid var(--line); border-radius: 16px; padding: 28px; }
.lp-price.featured { border-color: var(--blue); box-shadow: 0 16px 44px rgba(59,130,246,.16);
  position: relative; }
.lp-price.featured::before { content: "Most popular"; position: absolute; top: -11px; left: 28px;
  background: var(--blue); color: #fff; font-size: 0.68rem; font-weight: 700; letter-spacing: .5px;
  text-transform: uppercase; padding: 4px 11px; border-radius: 999px; }
.lp-price-name { font-size: 0.85rem; text-transform: uppercase; letter-spacing: 1px; color: var(--muted); }
.lp-price-amt { font-family: var(--mono); font-size: 2.4rem; font-weight: 700; color: var(--ink);
  margin: 10px 0 2px; letter-spacing: -1px; }
.lp-price-amt small { font-size: 0.9rem; color: var(--muted); font-weight: 400; letter-spacing: 0; }
.lp-price-quota { color: var(--emerald); font-family: var(--mono); font-size: 0.85rem; margin-bottom: 18px; }
.lp-price ul { list-style: none; margin: 0 0 24px; padding: 0; display: flex; flex-direction: column; gap: 10px; }
.lp-price li { font-size: 0.9rem; padding-left: 24px; position: relative; }
.lp-price li::before { content: "✓"; position: absolute; left: 0; color: var(--emerald); font-weight: 800; }
.lp-price .lp-btn { width: 100%; justify-content: center; }

/* Closing CTA */
.lp-close { text-align: center; background: linear-gradient(180deg, var(--panel), var(--ground));
  border: 1px solid var(--line); border-radius: 20px; padding: 56px 24px; margin: 24px 0 64px; }
.lp-close .lp-h2 { margin-bottom: 10px; }
.lp-close .lp-sub { margin: 0 auto 28px; }
.lp-close-cta { display: flex; gap: 14px; justify-content: center; flex-wrap: wrap; }

/* Footer */
.lp-foot { border-top: 1px solid var(--line); padding: 28px 0 48px; color: var(--muted); font-size: 0.82rem;
  display: flex; justify-content: space-between; flex-wrap: wrap; gap: 12px; }

@media (max-width: 860px) {
  .lp-hero { grid-template-columns: 1fr; gap: 36px; padding: 32px 0; }
  .lp-stats { grid-template-columns: repeat(2, 1fr); }
  .lp-signals { grid-template-columns: repeat(2, 1fr); }
  .lp-steps, .lp-prices { grid-template-columns: 1fr; }
  .lp-price.featured { order: -1; }
}
@media (max-width: 480px) {
  .lp-signals { grid-template-columns: 1fr; }
}
@media (prefers-reduced-motion: reduce) {
  .lp-dot { animation: none; }
  .lp-row.fresh { animation: none; }
}
`;

// Sample businesses for the live feed — realistic FL names across categories.
const SAMPLE = [
  { name: 'Coastal Char Kitchen', cat: 'Restaurant', city: 'Tampa', badge: 'Pre-opening', cls: 'b-gold' },
  { name: 'Neon Palm Lounge', cat: 'Bar', city: 'Miami', badge: 'New license', cls: 'b-blue' },
  { name: 'SunState Roofing Co.', cat: 'Roofing', city: 'Orlando', badge: 'New license', cls: 'b-blue' },
  { name: 'Harbor View Suites', cat: 'Lodging', city: 'Naples', badge: 'New owner', cls: 'b-green' },
  { name: 'Rapid Comfort HVAC', cat: 'HVAC', city: 'Jacksonville', badge: 'New license', cls: 'b-blue' },
  { name: 'Bella Vista Trattoria', cat: 'Restaurant', city: 'Sarasota', badge: 'New owner', cls: 'b-green' },
  { name: 'Gulfstream Electric', cat: 'Electrical', city: 'Ft. Lauderdale', badge: 'New license', cls: 'b-blue' },
  { name: 'The Copper Still', cat: 'Bar', city: 'St. Petersburg', badge: 'Pre-opening', cls: 'b-gold' },
  { name: 'Everglade Plumbing', cat: 'Plumbing', city: 'Ft. Myers', badge: 'New license', cls: 'b-blue' },
  { name: 'Azure Pool & Spa', cat: 'Pool & Spa', city: 'Boca Raton', badge: 'New license', cls: 'b-blue' },
  { name: 'Mangrove Coffee House', cat: 'Restaurant', city: 'Key West', badge: 'Pre-opening', cls: 'b-gold' },
  { name: 'Summit General Contracting', cat: 'Contractor', city: 'Gainesville', badge: 'New license', cls: 'b-blue' },
];

const CATEGORIES = ['Restaurants', 'Bars & Liquor', 'Hotels & Lodging', 'General Contractors',
  'HVAC', 'Roofing', 'Plumbing', 'Electrical', 'Pool & Spa'];

const SIGNALS = [
  { badge: 'Pre-opening', cls: 'b-gold', spark: true, title: 'The golden window',
    body: 'Facilities in build-out, before the doors open. They’re actively choosing vendors — and nobody else knows they exist yet.' },
  { badge: 'New owner', cls: 'b-green', title: 'Fresh decision-maker',
    body: 'A business that just changed hands. New owners re-evaluate every vendor relationship from scratch.' },
  { badge: 'New license', cls: 'b-blue', title: 'Just went live',
    body: 'A newly issued state license — restaurant, liquor, contractor, and more. Recently operational, still setting up.' },
  { badge: 'Inbound', cls: 'b-green', title: 'Hand-raiser',
    body: 'The owner submitted their own details and agreed to be contacted. Verified info, expecting your call.' },
];

const PRICING = [
  { name: 'Free', amt: '$0', per: '', quota: '50 leads / month',
    features: ['Every category & county', 'Daily-fresh listings', 'CSV export', 'Search & geo filters'],
    cta: 'Start free', variant: 'ghost' },
  { name: 'Pro', amt: '$49', per: '/mo', quota: '500 leads / month', featured: true,
    features: ['Everything in Free', 'All export formats', 'Autodialer export', 'Notes & status pipeline', 'Priority AI enrichment'],
    cta: 'Start Pro', variant: 'primary' },
  { name: 'Enterprise', amt: 'Custom', per: '', quota: '5,000+ leads / month',
    features: ['Everything in Pro', 'Team seats & sharing', 'Priority ingestion', 'API access', 'Dedicated support'],
    cta: 'Talk to us', variant: 'ghost' },
];

const prefersReduced = () =>
  typeof window !== 'undefined' &&
  window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function LiveFeed() {
  const reduced = prefersReduced();
  const [rows, setRows] = useState(() =>
    SAMPLE.slice(0, 5).map((s, i) => ({ ...s, id: i, t: clockAt(-i * 3) }))
  );

  useEffect(() => {
    if (reduced) return;
    let idx = 5;
    let uid = 1000;
    const timer = setInterval(() => {
      const s = SAMPLE[idx % SAMPLE.length];
      idx += 1; uid += 1;
      setRows((prev) => [{ ...s, id: uid, t: clockNow(), fresh: true }, ...prev].slice(0, 5));
    }, 2400);
    return () => clearInterval(timer);
  }, [reduced]);

  return (
    <div className="lp-feed" aria-hidden="true">
      <div className="lp-feed-head">
        <span className="lp-feed-title">New in Florida · today</span>
        <span className="lp-feed-live"><span className="lp-dot" /> LIVE</span>
      </div>
      <ul className="lp-feed-list">
        {rows.map((r) => (
          <li key={r.id} className={`lp-row${r.fresh ? ' fresh' : ''}`}>
            <div className="lp-row-main">
              <div className="lp-row-name">{r.name}</div>
              <div className="lp-row-meta">{r.cat} · {r.city}, FL</div>
            </div>
            <span className={`lp-badge ${r.cls}`}>{r.badge}</span>
            <span className="lp-row-time">{r.t}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function clockNow() {
  const d = new Date();
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}
function clockAt(minsOffset) {
  const d = new Date(Date.now() + minsOffset * 60000);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

export default function LandingPage({ isAuthed }) {
  const navigate = useNavigate();
  const go = () => navigate('/app');

  return (
    <div className="lp">
      <style>{CSS}</style>

      <nav className="lp-nav">
        <div className="lp-logo">AMANAR <span>LEAD ENGINE</span></div>
        <div className="lp-nav-actions">
          {isAuthed ? (
            <button className="lp-btn lp-btn-primary" onClick={go}>Go to dashboard →</button>
          ) : (
            <>
              <a className="lp-signin" href="/app" onClick={(e) => { e.preventDefault(); go(); }}>Sign in</a>
              <button className="lp-btn lp-btn-primary" onClick={go}>Start free</button>
            </>
          )}
        </div>
      </nav>

      {/* Hero */}
      <header className="lp-wrap">
        <div className="lp-hero">
          <div>
            <span className="lp-eyebrow"><span className="lp-dot" /> Updated every business morning</span>
            <h1 className="lp-h1">Reach Florida&rsquo;s newest businesses <em>before</em> your competition does.</h1>
            <p className="lp-lede">
              Every new restaurant, bar, hotel, and contractor in Florida — pulled from
              state licensing records daily, often before they&rsquo;ve opened or built a website.
            </p>
            <div className="lp-hero-cta">
              <button className="lp-btn lp-btn-primary lp-btn-lg" onClick={go}>Start free — 50 leads</button>
              <a className="lp-btn lp-btn-ghost lp-btn-lg" href="/new/food/tampa">See live listings</a>
            </div>
            <p className="lp-hero-note">No credit card · ~750 new businesses added every weekday</p>
          </div>
          <LiveFeed />
        </div>

        {/* Stat strip */}
        <div className="lp-stats">
          <div className="lp-stat"><div className="lp-stat-n">750+</div><div className="lp-stat-l">new businesses / weekday</div></div>
          <div className="lp-stat"><div className="lp-stat-n">9</div><div className="lp-stat-l">categories statewide</div></div>
          <div className="lp-stat"><div className="lp-stat-n">67</div><div className="lp-stat-l">Florida counties</div></div>
          <div className="lp-stat"><div className="lp-stat-n">9AM</div><div className="lp-stat-l">refreshed daily, ET</div></div>
        </div>
      </header>

      {/* The signal (differentiator) */}
      <section className="lp-wrap lp-section">
        <div className="lp-kicker">Timing is the product</div>
        <h2 className="lp-h2">Every lead tells you how early you are.</h2>
        <p className="lp-sub">
          Anyone can sell you a list of businesses. We tell you which ones are still
          choosing their vendors — the difference between a cold call and a warm one.
        </p>
        <div className="lp-signals">
          {SIGNALS.map((s) => (
            <div key={s.badge} className={`lp-signal${s.spark ? ' spark' : ''}`}>
              <span className={`lp-badge ${s.cls}`}>{s.badge}</span>
              <h3>{s.title}</h3>
              <p>{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="lp-wrap lp-section" style={{ paddingTop: 0 }}>
        <div className="lp-kicker">How it works</div>
        <h2 className="lp-h2">From signup to first call in minutes.</h2>
        <div className="lp-steps" style={{ marginTop: 28 }}>
          <div className="lp-step">
            <div className="lp-step-n">01 / Filter</div>
            <h3>Pick your territory</h3>
            <p>Choose a category and narrow by city or county. Toggle &ldquo;has contact info&rdquo; to see only leads you can reach today.</p>
          </div>
          <div className="lp-step">
            <div className="lp-step-n">02 / Work</div>
            <h3>Run your pipeline</h3>
            <p>Take a lead into your private workflow, log calls, set status, and add notes. The shared pool stays shared; your work stays yours.</p>
          </div>
          <div className="lp-step">
            <div className="lp-step-n">03 / Export</div>
            <h3>Take it anywhere</h3>
            <p>Export to CSV for your CRM, or straight into autodialer formats for a calling session. Your filters come with you.</p>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="lp-wrap lp-section" style={{ paddingTop: 0 }}>
        <div className="lp-kicker">Coverage</div>
        <h2 className="lp-h2">Nine categories, all of Florida.</h2>
        <p className="lp-sub">Cross-referenced with corporate filings, so every lead carries its registration details and often the owner or registered agent.</p>
        <div className="lp-cats">
          {CATEGORIES.map((c) => <span key={c} className="lp-cat">{c}</span>)}
        </div>
      </section>

      {/* Pricing */}
      <section className="lp-wrap lp-section" style={{ paddingTop: 0 }} id="pricing">
        <div className="lp-kicker">Pricing</div>
        <h2 className="lp-h2">Start free. Upgrade when it pays for itself.</h2>
        <p className="lp-sub">Quotas count only the leads you <em>take</em> — browsing, filtering, and viewing are always unlimited. Change plans anytime.</p>
        <div className="lp-prices">
          {PRICING.map((p) => (
            <div key={p.name} className={`lp-price${p.featured ? ' featured' : ''}`}>
              <div className="lp-price-name">{p.name}</div>
              <div className="lp-price-amt">{p.amt}<small>{p.per}</small></div>
              <div className="lp-price-quota">{p.quota}</div>
              <ul>{p.features.map((f) => <li key={f}>{f}</li>)}</ul>
              <button className={`lp-btn lp-btn-${p.variant}`} onClick={go}>{p.cta}</button>
            </div>
          ))}
        </div>
      </section>

      {/* Closing CTA */}
      <div className="lp-wrap">
        <div className="lp-close">
          <div className="lp-kicker" style={{ color: 'var(--emerald)' }}>Get started</div>
          <h2 className="lp-h2">Tomorrow&rsquo;s customers are registering right now.</h2>
          <p className="lp-sub">Create a free account and pull your first territory in under five minutes.</p>
          <div className="lp-close-cta">
            <button className="lp-btn lp-btn-primary lp-btn-lg" onClick={go}>Start free — 50 leads</button>
            <a className="lp-btn lp-btn-ghost lp-btn-lg" href="/new/food/tampa">Browse live listings</a>
          </div>
        </div>
      </div>

      <footer className="lp-wrap lp-foot">
        <span>© {new Date().getFullYear()} Amanar · Lead Engine · <a href="/guide" style={{ color: 'var(--muted)', textDecoration: 'underline' }}>Customer Guide</a> · <a href="/terms" style={{ color: 'var(--muted)', textDecoration: 'underline' }}>Terms &amp; Acceptable Use</a></span>
        <span>Compiled from Florida DBPR &amp; Division of Corporations public records.</span>
      </footer>
    </div>
  );
}
